import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type { ReactNode } from 'react';
import { DANGER_BUTTON_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from './accessible-buttons';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Spiegazione della conseguenza dell'azione. */
  children: ReactNode;
  confirmLabel: string;
  /** Colore del pulsante di conferma: `error` per le azioni distruttive. */
  confirmColor?: 'primary' | 'error';
  /** Errore restituito dall'azione, mostrato nel dialogo senza chiuderlo. */
  error?: string | null;
  /** Se vero la conferma è disabilitata (azione in corso o impedita). */
  busy?: boolean;
  /** Se vero l'azione è impedita: resta solo «Annulla». */
  blocked?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * A dialogo chiuso, finita l'uscita: per portare il focus altrove quando il comando che l'ha aperto
   * sparisce o non è il punto giusto (#159). Con questa funzione il focus non torna al comando.
   */
  onExited?: () => void;
}

/**
 * Dialogo di conferma per le azioni con conseguenze: spiega, mostra l'errore, poi conferma. Pulsanti
 * con contrasto AAA, focus visibile e bersagli di 44 px (`accessible-buttons.ts`).
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  confirmColor = 'primary',
  error = null,
  busy = false,
  blocked = false,
  onConfirm,
  onClose,
  onExited,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="confirm-dialog-title"
      disableRestoreFocus={onExited !== undefined}
      slotProps={{ transition: { onExited } }}
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{children}</DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={SECONDARY_BUTTON_SX}>
          {blocked ? 'Chiudi' : 'Annulla'}
        </Button>
        {!blocked && (
          <Button
            onClick={onConfirm}
            color={confirmColor}
            variant="contained"
            disabled={busy}
            sx={confirmColor === 'error' ? DANGER_BUTTON_SX : PRIMARY_BUTTON_SX}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
